/**
 * Target Language Selector Component
 * Dropdown for selecting which language to import from a multi-language file
 * Shows only languages configured for the selected application type
 */

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/core/components/ui/select';
import { Label } from '@/core/components/ui/label';
import { useApplicationLanguagesStore } from '../../stores/application-languages-store';
import { useTranslationStore } from '../../stores/translation-store';
import { useEffect } from 'react';

interface TargetLanguageSelectorProps {
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
  className?: string;
}

export function TargetLanguageSelector({
  value,
  onChange,
  disabled = false,
  className = '',
}: TargetLanguageSelectorProps) {
  const { selectedAppType } = useTranslationStore();
  const { applicationLanguages, fetchApplicationLanguages, isLoading } = useApplicationLanguagesStore();

  // Fetch languages for the selected app type
  useEffect(() => {
    if (selectedAppType) {
      fetchApplicationLanguages(selectedAppType);
    }
  }, [selectedAppType, fetchApplicationLanguages]);

  // Build language options from application-specific languages
  const languageOptions = applicationLanguages.map((lang) => ({
    code: lang.languageCode,
    name: lang.languageName || lang.nativeName || lang.languageCode,
  }));

  // Sort alphabetically by name
  languageOptions.sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className={`space-y-2 ${className}`}>
      <Label>Target Language (Optional)</Label>
      <Select
        value={value || 'all'}
        onValueChange={(val) => onChange(val === 'all' ? null : val)}
        disabled={disabled || isLoading}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder="All Languages" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Languages</SelectItem>
          {languageOptions.map((lang) => (
            <SelectItem key={lang.code} value={lang.code}>
              {lang.name} ({lang.code})
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        For import only: Select a specific language to import only that language from the file.
        Leave as "All Languages" to import all languages. Does not affect template download or export.
      </p>
    </div>
  );
}
